/**
 * @fileoverview Pure resolution of letterhead/cover-page logo slots to concrete image URLs.
 * @module document-render/logoResolver
 *
 * Responsibilities: - Resolve a LogoDescriptor (department vs uploaded, show flag) to the image URL
 *                     that the shared renderer should emit for that header logo slot.
 *                   - Resolve a department's effective primary/secondary logo URL from its branding
 *                     block (uploaded-ref precedence over the legacy string), independent of any
 *                     document-level "show logo" flag, so callers always have the raw asset URL to
 *                     hand to a band slot regardless of legacy gating (see applyLayoutHeaderLogos).
 * Dependencies:     - ./documentLayout (LogoDescriptor).
 * Architecture:     - Pure + framework-agnostic so preview (React), the worker (PDF) and tests share
 *                     one resolution rule. Callers supply the department logo URL (from the theme)
 *                     and a resolver for uploaded-file ids; this module owns only the branch logic.
 * Notes:            - Returns undefined when the slot is hidden or its source cannot be resolved, so
 *                     callers simply omit the <img> — no broken-image placeholders.
 *                   - resolveDepartmentLogoUrl/resolveDepartmentSecondLogoUrl intentionally duplicate
 *                     the identical 2-line precedence rule in
 *                     web/packages/core-domain/src/reporting/brandingLogo.ts. This is a deliberate,
 *                     documented trade-off (see that file's header comment): document-render has no
 *                     runtime dependencies and is consumed by the Node worker, so it cannot import a
 *                     web-only package without creating a wrong-direction/circular dependency
 *                     (core-domain already imports assembleDocumentRenderData/applyLayoutHeaderLogos
 *                     from here). Keep both copies in lockstep if the precedence rule ever changes.
 */

import type { LogoDescriptor, DocumentLayout, Band } from './documentLayout';
import type { DocumentRenderData } from './documentConfig';

/**
 * @contract LogoResolutionContext
 * Purpose: External inputs needed to turn a LogoDescriptor into a URL.
 * Fields:
 * - departmentLogoUrl: The department's effective PRIMARY logo URL, already resolved via
 *   resolveDepartmentLogoUrl (undefined if the department has none). Must be the raw, ungated
 *   asset URL — NOT a value that has already been gated by a document-level "show logo" flag,
 *   since the band descriptor's own `show` is the only visibility gate once a canvas layout exists.
 * - departmentSecondLogoUrl: The department's effective SECONDARY logo URL, same rule as above.
 * - resolveUploadedFileUrl: Maps an UploadedFile id to a displayable URL (blob/CDN), or undefined.
 * Rules: Pure inputs; the resolver must not perform I/O during render (pre-resolve URLs upstream).
 */
export interface LogoResolutionContext {
  departmentLogoUrl?: string;
  departmentSecondLogoUrl?: string;
  resolveUploadedFileUrl?: (uploadedFileId: string) => string | undefined;
}

/**
 * @contract DepartmentBrandingLike
 * Purpose: Structural shape of the branding block consumed when resolving a department's logos.
 * Fields:  logo/secondLogo — uploaded logo refs (only `.blobUrl` is consulted); logoUrl — legacy
 *          string fallback for the primary slot only (there was never a legacy secondLogoUrl).
 * Rules:   `logo.blobUrl` beats `logoUrl`; `secondLogo.blobUrl` has no legacy fallback.
 * Sync:    local-first; mirrors the Mongo tenant document and the Dexie system_tenant_ref projection.
 */
export interface DepartmentBrandingLike {
  logo?: { blobUrl?: string };
  secondLogo?: { blobUrl?: string };
  logoUrl?: string;
}

/**
 * @process resolveDepartmentLogoUrl
 * Steps:           Prefer the uploaded primary-slot ref's blobUrl; else fall back to the legacy
 *                  `logoUrl` string; else undefined. NOT gated by any "show logo" flag — callers
 *                  decide visibility via the LogoDescriptor.show / HeaderConfig.showLogo flags.
 * Inputs:          branding block (or undefined/loosely-typed Mongo/Dexie record).
 * Outputs:         a logo URL/blob-name string, or undefined when none set.
 * Failure Handling:never throws; missing fields collapse to undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolveDepartmentLogoUrl(
  branding: DepartmentBrandingLike | undefined,
): string | undefined {
  if (!branding) return undefined;
  return branding.logo?.blobUrl || branding.logoUrl || undefined;
}

/**
 * @process resolveDepartmentSecondLogoUrl
 * Steps:           Resolve the secondary-slot uploaded ref's blobUrl. NOT gated by any "show
 *                  logo" flag (see resolveDepartmentLogoUrl).
 * Inputs:          branding block (or undefined/loosely-typed Mongo/Dexie record).
 * Outputs:         a logo URL/blob-name string, or undefined when none set.
 * Failure Handling:never throws.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolveDepartmentSecondLogoUrl(
  branding: DepartmentBrandingLike | undefined,
): string | undefined {
  if (!branding) return undefined;
  return branding.secondLogo?.blobUrl || undefined;
}

/**
 * @process resolveLogo
 * Steps:   Honour `show`; for 'department' pick the primary or secondary department
 *          logo URL based on `departmentLogoSlot` (defaults to primary); for 'uploaded'
 *          resolve the referenced file id via the context resolver.
 * Inputs:  descriptor (may be undefined), context.
 * Outputs: a URL string, or undefined when the slot should be omitted.
 * Idempotency: REQUIRED (pure function of its inputs).
 */
export function resolveLogo(
  descriptor: LogoDescriptor | undefined,
  context: LogoResolutionContext,
): string | undefined {
  if (!descriptor || descriptor.show === false) return undefined;
  if (descriptor.source === 'department') {
    if (descriptor.departmentLogoSlot === 'secondary') {
      return context.departmentSecondLogoUrl || undefined;
    }
    return context.departmentLogoUrl || undefined;
  }
  if (descriptor.source === 'uploaded') {
    if (!descriptor.uploadedFileId) return undefined;
    return context.resolveUploadedFileUrl?.(descriptor.uploadedFileId) || undefined;
  }
  return undefined;
}

/** A sensible default descriptor for a newly shown logo slot (department source, primary logo). */
export function defaultLogoDescriptor(): LogoDescriptor {
  return { source: 'department', show: true, departmentLogoSlot: 'primary' };
}

/**
 * @process collectUploadedLogoIds
 * Steps: Walk a layout's header + body + cover bands, gather the uploadedFileId of every
 *        primary/secondary logo slot whose source is 'uploaded', de-duplicated.
 * Inputs: DocumentLayout.
 * Outputs: Unique uploaded-file id list (empty when only department logos are used).
 * Failure Handling: None — pure, null-safe over missing bands/slots.
 * Idempotency: REQUIRED (pure). Used by the builder pages (resolve thumbnails/preview URLs)
 *              and the PDF worker (resolve blob URLs) so both share one collection rule.
 */
export function collectUploadedLogoIds(layout: DocumentLayout): string[] {
  const bands: (Band | null | undefined)[] = [
    layout.header,
    ...layout.body,
    ...(layout.coverLetter?.bands ?? []),
  ];
  const ids = new Set<string>();
  for (const band of bands) {
    if (!band) continue;
    for (const slot of [band.primaryLogo, band.secondaryLogo]) {
      if (slot?.source === 'uploaded' && slot.uploadedFileId) ids.add(slot.uploadedFileId);
    }
  }
  return [...ids];
}

/**
 * @process remapUploadedLogoIds
 * Steps: Walk a layout's header + body + cover bands and, for every primary/secondary logo
 *        slot whose source is 'uploaded' and whose `uploadedFileId` has an entry in `idMap`,
 *        replace it with the mapped id. Slots whose id is absent from the map are left as-is.
 * Inputs: DocumentLayout, idMap (old uploaded-file id → new uploaded-file id).
 * Outputs: A new DocumentLayout with remapped logo slot ids (structurally cloned bands; the
 *          input is never mutated).
 * Failure Handling: None — pure, null-safe over missing bands/slots; unmapped ids pass through.
 * Idempotency: REQUIRED (pure). Used by library→tenant adoption to re-point a copied layout's
 *              uploaded logos onto the freshly-cloned tenant uploaded-file records, sharing the
 *              same band-traversal definition as collectUploadedLogoIds (DRY).
 */
export function remapUploadedLogoIds(
  layout: DocumentLayout,
  idMap: Record<string, string>,
): DocumentLayout {
  const remapSlot = (slot: LogoDescriptor | undefined): LogoDescriptor | undefined => {
    if (!slot || slot.source !== 'uploaded' || !slot.uploadedFileId) return slot;
    const mapped = idMap[slot.uploadedFileId];
    if (!mapped) return slot;
    return { ...slot, uploadedFileId: mapped };
  };
  const remapBand = <T extends Band | null | undefined>(band: T): T => {
    if (!band) return band;
    return { ...band, primaryLogo: remapSlot(band.primaryLogo), secondaryLogo: remapSlot(band.secondaryLogo) };
  };

  return {
    ...layout,
    header: remapBand(layout.header),
    body: layout.body.map((b) => remapBand(b)),
    coverLetter: layout.coverLetter
      ? { ...layout.coverLetter, bands: layout.coverLetter.bands.map((b) => remapBand(b)) }
      : layout.coverLetter,
  };
}

/** The letterhead/cover band that owns the header logo slots, if any. */
function findLetterheadBand(layout: DocumentLayout): Band | undefined {
  if (layout.header && layout.header.kind === 'letterhead') return layout.header;
  const bodyLetterhead = layout.body.find((b) => b.kind === 'letterhead');
  if (bodyLetterhead) return bodyLetterhead;
  return layout.coverLetter?.bands.find((b) => b.kind === 'letterhead' || b.kind === 'coverPage');
}

/**
 * @process applyLayoutHeaderLogos
 * Steps:   Read the layout's letterhead/cover band logo descriptors (primaryLogo/secondaryLogo)
 *          and resolve each onto the render data's header logo URLs via `resolveLogo`, using the
 *          caller-supplied RAW `context.departmentLogoUrl`/`departmentSecondLogoUrl` (see
 *          LogoResolutionContext) for `department`-sourced slots and `resolveUploadedFileUrl` for
 *          `uploaded`-sourced slots. A hidden slot (`show: false`) is omitted.
 * Inputs:  layout (band model), data (resolved render data), context (raw department logo URLs +
 *          uploaded-file URL resolver — see LogoResolutionContext for why these must be ungated).
 * Outputs: a new DocumentRenderData (header logos overridden) — or the input unchanged when there
 *          is no header section or no letterhead band with logo descriptors.
 * Idempotency: REQUIRED (pure function of its inputs).
 * Notes:   Callers must pass the department's RAW logo URLs (via resolveDepartmentLogoUrl /
 *          resolveDepartmentSecondLogoUrl), not a value already gated by a legacy "show logo"
 *          document flag — a band slot's own `show` is the only visibility gate once a canvas
 *          layout exists. Passing a pre-gated URL here silently breaks `department`-sourced slots
 *          whenever the legacy flag defaults to false (the G-LOGO-05 regression: a "show department
 *          logo" secondary slot rendered nothing because `header.secondLogoUrl` was already
 *          undefined by the time it reached this function).
 */
export function applyLayoutHeaderLogos(
  layout: DocumentLayout,
  data: DocumentRenderData,
  context: LogoResolutionContext,
): DocumentRenderData {
  if (!data.header) return data;
  const band = findLetterheadBand(layout);
  if (!band || (!band.primaryLogo && !band.secondaryLogo)) return data;

  const header = { ...data.header };
  if (band.primaryLogo) {
    header.logoUrl = resolveLogo(band.primaryLogo, context);
  }
  if (band.secondaryLogo) {
    header.secondLogoUrl = resolveLogo(band.secondaryLogo, context);
  }
  return { ...data, header };
}
